import { apiNodeRequest } from '@/store/core/main'
import { normalizeReel, serializeReel } from './reel-studio-logic'

const STORAGE_KEY = 'happily-townhall-reels-v1'
const REMOTE_REEL_API_URL = 'https://us-central1-people2-admin.cloudfunctions.net/townhallReelsApi/v1/reels'
const configuredReelApiUrl = () => (process.env.VUE_APP_REEL_API_URL || '').trim()
const reelApiUrl = () => {
  const configuredUrl = configuredReelApiUrl()
  const url = configuredUrl.toLowerCase() === 'remote' ? REMOTE_REEL_API_URL : configuredUrl || REMOTE_REEL_API_URL
  return url.replace(/\/+$/, '')
}
const localMode = () => process.env.NODE_ENV !== 'production' && !configuredReelApiUrl()

const readLocal = () => {
  if (typeof window === 'undefined') return []
  try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]').map(normalizeReel) } catch (_) { return [] }
}

const writeLocal = (items) => {
  if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
}

const localSave = (value) => {
  const items = readLocal()
  const now = new Date().toISOString()
  const identity = { id: value.id || `reel_${Date.now().toString(36)}`, version: (value.version || 0) + 1, updatedAt: now }
  const persisted = normalizeReel({ ...serializeReel(value), ...identity })
  const reel = normalizeReel({ ...value, ...identity })
  const index = items.findIndex((item) => item.id === persisted.id)
  if (index === -1) items.unshift(persisted)
  else items.splice(index, 1, persisted)
  writeLocal(items)
  return reel
}

const request = async (config) => {
  const prefix = reelApiUrl()
  return apiNodeRequest({ ...config, url: `${prefix}${config.path || ''}` }).then((response) => response.data)
}

export const reelApi = {
  async list() {
    if (localMode()) return readLocal()
    const items = await request({ method: 'GET' })
    return (Array.isArray(items) ? items : []).map(normalizeReel)
  },
  async get(id) {
    if (localMode()) return readLocal().find((item) => item.id === id) || null
    const item = await request({ method: 'GET', path: `/${id}` })
    return item ? normalizeReel(item) : item
  },
  async save(value) {
    if (localMode()) return localSave(value)
    const payload = serializeReel(value)
    if (!value.id) return request({ method: 'POST', data: payload })
    return request({ method: 'PATCH', path: `/${value.id}`, data: payload })
  },
  async createAssetSession(id, asset) {
    if (localMode()) return { assetId: `local_${Date.now().toString(36)}`, uploadUrl: '', asset }
    return request({ method: 'POST', path: `/${id}/assets`, data: asset })
  },
  async uploadAsset(id, file, kind) {
    const session = await this.createAssetSession(id, {
      file_name: file.name,
      content_type: file.type,
      size_bytes: file.size,
      kind,
    })
    if (session.uploadUrl) {
      const response = await fetch(session.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      if (!response.ok) throw new Error(`Asset upload failed (${response.status})`)
    }
    return { ...session.asset, id: session.assetId, status: 'ready', name: file.name }
  },
  async remove(id) {
    if (localMode()) { writeLocal(readLocal().filter((item) => item.id !== id)); return true }
    return request({ method: 'DELETE', path: `/${id}` })
  },
  async action(id, action, data = {}) {
    if (localMode()) {
      const source = readLocal().find((item) => item.id === id)
      if (!source) throw new Error('Reel not found')
      if (action === 'analytics') return source.analytics || { views: 0, completions: 0, reactions: 0 }
      if (action === 'duplicate') return localSave({ ...source, id: '', title: `${source.title || 'Untitled reel'} copy`, publicationStatus: 'draft', renderStatus: 'not_requested', archived: false, archivedAt: null, archivePreviousStatus: null, publishedAt: null })
      const patch = action === 'unpublish'
        ? { publicationStatus: 'unpublished' }
        : action === 'archive'
          ? { archived: true, archivedAt: new Date().toISOString(), archivePreviousStatus: source.publicationStatus, publicationStatus: source.publicationStatus === 'published' ? 'unpublished' : source.publicationStatus === 'scheduled' ? 'draft' : source.publicationStatus, scheduledAt: null }
          : action === 'restore'
            ? { archived: false, archivedAt: null, archivePreviousStatus: null, publicationStatus: ['draft', 'scheduled'].includes(source.archivePreviousStatus) ? 'draft' : 'unpublished' }
        : action === 'publish'
          ? { publicationStatus: 'published', renderStatus: 'ready', publishedAt: new Date().toISOString() }
          : action === 'schedule'
            ? { publicationStatus: 'scheduled', scheduledAt: data.scheduledAt }
            : action === 'retry'
              ? { renderStatus: 'queued' }
              : action === 'cancel-render'
                ? { renderStatus: 'not_requested', renderError: null }
              : action === 'cancel-schedule'
                ? { publicationStatus: 'draft', scheduledAt: null }
                : {}
      return localSave({ ...source, ...patch })
    }
    return request({ method: action === 'analytics' ? 'GET' : 'POST', path: `/${id}/${action}`, ...(action === 'analytics' ? {} : { data }) })
  },
}

export default reelApi
